import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  alpha,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Fingerprint, ArrowForward, Radar } from '@mui/icons-material';
import axios from 'axios';
import { palette } from '../theme';

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.append('username', username.trim());
      params.append('password', password);

      const response = await axios.post(
        '/api/v1/auth/login',
        params,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );

      await onLogin(response.data.access_token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Identity verification failed. Check your credentials.');
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!username.trim()) {
      setError('Enter your operator ID so we know which account to recover.');
      return;
    }
    setError('');
    setRecovering(true);
    try {
      await axios.post('/api/v1/auth/forgot-password', { identifier: username.trim() });
      setRecoverySent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Recovery request could not be dispatched.');
    } finally {
      setRecovering(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: palette.background,
        px: 2,
        py: 5,
      }}
    >
      {/* ambient lighting */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(900px 620px at 22% 12%, ${alpha(palette.primary, 0.18)}, transparent 60%),
            radial-gradient(760px 540px at 82% 82%, ${alpha(palette.secondary, 0.16)}, transparent 62%),
            radial-gradient(1200px 700px at 50% 120%, ${alpha(palette.primary, 0.09)}, transparent 60%)
          `,
        }}
      />
      <Box
        className="holo-grid"
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.5,
          maskImage: 'radial-gradient(circle at 50% 40%, black 0%, transparent 76%)',
          WebkitMaskImage: 'radial-gradient(circle at 50% 40%, black 0%, transparent 76%)',
        }}
      />
      <Box className="holo-scan" sx={{ position: 'absolute', inset: 0, opacity: 0.6 }} />

      {/* decorative radar ring */}
      <Box
        sx={{
          position: 'absolute',
          width: { xs: 460, md: 760 },
          aspectRatio: '1',
          borderRadius: '50%',
          border: `1px solid ${alpha(palette.primary, 0.14)}`,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          animation: 'holoSpin 42s linear infinite',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: '14%',
            borderRadius: '50%',
            border: `1px solid ${alpha(palette.secondary, 0.12)}`,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: '32%',
            borderRadius: '50%',
            border: `1px solid ${alpha(palette.primary, 0.12)}`,
          },
        }}
      />

      <Box sx={{ position: 'relative', width: '100%', maxWidth: 440 }}>
        {/* brand header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 62,
              height: 62,
              margin: '0 auto 1.5rem',
              borderRadius: 3,
              display: 'grid',
              placeItems: 'center',
              background: `linear-gradient(140deg, ${alpha(palette.primary, 0.28)}, ${alpha(palette.secondary, 0.18)})`,
              border: `1px solid ${alpha(palette.primary, 0.4)}`,
              boxShadow: `0 0 40px ${alpha(palette.primary, 0.35)}, inset 0 1px 0 ${alpha('#FFFFFF', 0.12)}`,
            }}
          >
            <Radar sx={{ fontSize: 30, color: palette.primary }} />
          </Box>
          <Typography
            sx={{
              fontSize: '1.65rem',
              fontWeight: 700,
              letterSpacing: '0.16em',
              lineHeight: 1.1,
              background: `linear-gradient(120deg, ${palette.text}, ${palette.primary})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            BLUE NODE
          </Typography>
          <Typography sx={{ mt: 0.85, fontSize: '0.68rem', letterSpacing: '0.3em', color: 'text.secondary' }}>
            IOT COMMAND CENTER
          </Typography>
        </Box>

        {/* glass console */}
        <Box
          sx={{
            position: 'relative',
            borderRadius: 5,
            p: { xs: 2.75, sm: 4 },
            border: `1px solid ${alpha(palette.primary, 0.2)}`,
            background: `linear-gradient(160deg, ${alpha('#0E1730', 0.86)}, ${alpha('#050816', 0.92)})`,
            backdropFilter: 'blur(30px) saturate(140%)',
            WebkitBackdropFilter: 'blur(30px) saturate(140%)',
            boxShadow: `0 40px 90px -40px rgba(0,0,0,0.95), inset 0 1px 0 ${alpha('#FFFFFF', 0.06)}`,
            // A one-shot entrance: an infinite float would keep the panel
            // drifting under the pointer and make the controls harder to hit.
            animation: 'holoEnter 700ms cubic-bezier(0.22, 0.9, 0.28, 1) both',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: 5,
              pointerEvents: 'none',
              background: `radial-gradient(120% 70% at 50% -10%, ${alpha(palette.primary, 0.16)}, transparent 60%)`,
            },
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <Typography sx={{ fontSize: '0.66rem', letterSpacing: '0.22em', color: 'text.secondary', mb: 0.5 }}>
              SECURE ACCESS
            </Typography>
            <Typography sx={{ fontSize: '1.12rem', fontWeight: 700, mb: 3 }}>
              Authenticate to continue
            </Typography>

            {error && (
              <Box
                sx={{
                  mb: 2.25,
                  px: 1.75,
                  py: 1.15,
                  borderRadius: 2.5,
                  fontSize: '0.78rem',
                  color: palette.danger,
                  background: alpha(palette.danger, 0.09),
                  border: `1px solid ${alpha(palette.danger, 0.28)}`,
                }}
              >
                {error}
              </Box>
            )}

            {recoverySent && (
              <Box
                sx={{
                  mb: 2.25,
                  px: 1.75,
                  py: 1.15,
                  borderRadius: 2.5,
                  fontSize: '0.78rem',
                  color: palette.success,
                  background: alpha(palette.success, 0.09),
                  border: `1px solid ${alpha(palette.success, 0.28)}`,
                }}
              >
                If the account exists, a recovery instruction has been dispatched.
              </Box>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <TextField
                inputRef={inputRef}
                label="Operator ID"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                required
                fullWidth
                autoComplete="username"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Fingerprint sx={{ fontSize: 18, color: focused ? palette.primary : 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Passphrase"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                fullWidth
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword((current) => !current)}
                        edge="end"
                        sx={{ color: 'text.secondary' }}
                      >
                        {showPassword ? <VisibilityOff sx={{ fontSize: 17 }} /> : <Visibility sx={{ fontSize: 17 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -0.5 }}>
                <Button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={recovering}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.74rem',
                    letterSpacing: '0.06em',
                    '&:hover': { color: palette.primary, background: 'transparent' },
                  }}
                >
                  {recovering ? 'Dispatching…' : 'Forgot passphrase?'}
                </Button>
              </Box>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                endIcon={!loading && <ArrowForward />}
                sx={{
                  mt: 1,
                  py: 1.4,
                  borderRadius: 3,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  fontSize: '0.82rem',
                }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : 'AUTHENTICATE'}
              </Button>
            </Box>

            <Box
              sx={{
                mt: 3,
                pt: 2.25,
                borderTop: `1px solid ${alpha(palette.primary, 0.1)}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: palette.success,
                  boxShadow: `0 0 10px ${palette.success}`,
                  animation: 'holoBlink 2.6s ease-in-out infinite',
                }}
              />
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', letterSpacing: '0.1em' }}>
                ENCRYPTED CHANNEL · RBAC ENFORCED
              </Typography>
            </Box>
          </Box>
        </Box>

        <Typography
          sx={{
            mt: 2.5,
            textAlign: 'center',
            fontSize: '0.68rem',
            color: 'text.secondary',
            letterSpacing: '0.1em',
          }}
        >
          ACCOUNT PROVISIONING IS ADMINISTRATOR CONTROLLED
        </Typography>
      </Box>
    </Box>
  );
}

export default Login;
